import { extractChargeItemsFromPayload } from "./warehouseReservationCharges";

export interface WarehouseGuestInfo {
  displayName: string | null;
  email: string | null;
  phone: string | null;
  country: string | null;
  companyName: string | null;
}

export interface WarehouseRoomLine {
  roomTypeLabel: string | null;
  rateLabel: string | null;
  roomName: string | null;
  numberOfRooms: number | null;
  adults: number | null;
  children: number | null;
  infants: number | null;
  amountAfterTax: number | null;
}

export interface WarehouseOccupancyInfo {
  adults: number | null;
  children: number | null;
  infants: number | null;
  nights: number | null;
}

interface PayloadRecord {
  guest?: {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
    country?: string;
    nationality?: string;
    company_name?: string | null;
  };
  guests?: Array<{
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
    country?: string;
    nationality?: string;
    company_name?: string | null;
  }>;
  room?: {
    room_name?: string;
    room_type_id?: string;
    room_type_name?: string;
    room_type_code?: string;
    room_type?: string;
    rate_id?: string;
    rate_name?: string;
    rate_code?: string;
    nb_adults?: number;
    nb_children?: number;
    nb_infants?: number;
    number_of_rooms?: number;
    amount_after_tax?: number;
  };
  room_types?: Array<{
    room_name?: string;
    room_type_id?: string;
    room_type_name?: string;
    room_type_code?: string;
    room_type?: string;
    rate_id?: string;
    rate_name?: string;
    rate_code?: string;
    nb_adults?: number;
    nb_children?: number;
    nb_infants?: number;
    number_of_rooms?: number;
    amount_after_tax?: number;
  }>;
  nb_adults?: number;
  nb_children?: number;
  nb_infants?: number;
  nights?: number;
  stay?: { nights?: number };
}

function asPayloadRecord(payload: unknown): PayloadRecord | null {
  if (!payload || typeof payload !== "object") {
    return null;
  }
  return payload as PayloadRecord;
}

function normalizeEmail(email: string | undefined | null): string | null {
  if (!email) {
    return null;
  }
  const value = email.trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? value : null;
}

function guestDisplayName(guest: PayloadRecord["guest"]): string | null {
  if (!guest) {
    return null;
  }
  const parts = [guest.first_name, guest.last_name].filter(Boolean);
  if (parts.length) {
    return parts.join(" ").trim();
  }
  return guest.company_name?.trim() || null;
}

function mapGuest(guest: PayloadRecord["guest"]): WarehouseGuestInfo | null {
  if (!guest) {
    return null;
  }

  return {
    displayName: guestDisplayName(guest),
    email: normalizeEmail(guest.email),
    phone: guest.phone?.trim() || null,
    country: guest.country || guest.nationality || null,
    companyName: guest.company_name?.trim() || null,
  };
}

export function extractGuestFromPayload(payload: unknown): WarehouseGuestInfo | null {
  const record = asPayloadRecord(payload);
  if (!record) {
    return null;
  }

  const primary = mapGuest(record.guest);
  if (primary?.email || primary?.displayName) {
    return primary;
  }

  for (const guest of record.guests ?? []) {
    const mapped = mapGuest(guest);
    if (mapped?.email || mapped?.displayName) {
      return mapped;
    }
  }

  return primary;
}

function mapRoomLine(room: NonNullable<PayloadRecord["room"]>): WarehouseRoomLine {
  return {
    roomTypeLabel:
      room.room_type_name ||
      room.room_type_code ||
      room.room_type ||
      room.room_type_id ||
      null,
    rateLabel: room.rate_name || room.rate_code || room.rate_id || null,
    roomName: room.room_name || null,
    numberOfRooms: room.number_of_rooms ?? null,
    adults: room.nb_adults ?? null,
    children: room.nb_children ?? null,
    infants: room.nb_infants ?? null,
    amountAfterTax: room.amount_after_tax ?? null,
  };
}

export function extractRoomLinesFromPayload(payload: unknown): WarehouseRoomLine[] {
  const record = asPayloadRecord(payload);
  if (!record) {
    return [];
  }

  if (record.room_types?.length) {
    return record.room_types.map(mapRoomLine);
  }

  if (record.room) {
    return [mapRoomLine(record.room)];
  }

  return [];
}

export function extractOccupancyFromPayload(payload: unknown): WarehouseOccupancyInfo {
  const record = asPayloadRecord(payload);
  if (!record) {
    return { adults: null, children: null, infants: null, nights: null };
  }

  const room = record.room;
  const roomTypes = record.room_types ?? [];

  const adults =
    record.nb_adults ??
    room?.nb_adults ??
    (roomTypes.length
      ? roomTypes.reduce((sum, item) => sum + (item.nb_adults ?? 0), 0) || null
      : null);

  const children =
    record.nb_children ??
    room?.nb_children ??
    (roomTypes.length
      ? roomTypes.reduce((sum, item) => sum + (item.nb_children ?? 0), 0) || null
      : null);

  const infants =
    record.nb_infants ??
    room?.nb_infants ??
    (roomTypes.length
      ? roomTypes.reduce((sum, item) => sum + (item.nb_infants ?? 0), 0) || null
      : null);

  return {
    adults,
    children,
    infants,
    nights: record.nights ?? record.stay?.nights ?? null,
  };
}

export function hasChargeBreakdown(payload: unknown): boolean {
  return extractChargeItemsFromPayload(payload).length > 0;
}
